/**
 * useRealtimeAudio
 *
 * React hook that captures microphone audio, resamples it to PCM16
 * at 24 000 Hz, and delivers Base64-encoded 100 ms chunks via callback.
 * Used by the Realtime API interview feature.
 *
 * Strategy:
 *   1. Prefer AudioWorklet (offloads DSP to a dedicated thread) — all
 *      modern browsers since Chrome 66 / Firefox 76 / Safari 14.1.
 *   2. Fall back to ScriptProcessorNode for older environments.
 *
 * Usage:
 *   const { startCapture, stopCapture, isCapturing, inputLevel, error } =
 *     useRealtimeAudio({
 *       onAudioChunk: (base64) => ws.send(JSON.stringify({
 *         type: 'input_audio_buffer.append',
 *         audio: base64,
 *       })),
 *       onLevelChange: setMicLevel,
 *     });
 */

import { useCallback, useRef, useState } from 'react';
import { logger } from '@/lib/logger';

// Vite transforms TypeScript worklet files referenced via `?url` and
// returns the resolved URL string (covered by the `vite/client` types that
// are already included via tsconfig `"types": ["vite/client"]`).
import audioProcessorUrl from './audio-processor.worklet?url';

// ── Types ─────────────────────────────────────────────────────────────────

export interface UseRealtimeAudioOptions {
  /** Called with a Base64-encoded PCM16 chunk (≈100 ms, 24 kHz mono). */
  onAudioChunk: (base64Pcm16: string) => void;
  /** Optional: called on every DSP block with the current RMS [0 – 1]. */
  onLevelChange?: (level: number) => void;
}

export interface UseRealtimeAudioResult {
  /** Request microphone access and start streaming. */
  startCapture: () => Promise<void>;
  /** Stop streaming and release microphone. */
  stopCapture: () => void;
  /** True while microphone is actively captured. */
  isCapturing: boolean;
  /** Current input level [0 – 1], useful for visualisation. */
  inputLevel: number;
  /**
   * Non-null when capture failed (e.g. permission denied, unsupported
   * browser).  Reset to null when `startCapture` is called again.
   */
  error: string | null;
}

// ── Helpers ───────────────────────────────────────────────────────────────

const TARGET_RATE = 24_000; // Hz

/** Convert an ArrayBuffer (Int16 PCM data) to a Base64 string. */
function pcm16BufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  // Process in 8 KB chunks to avoid call-stack limits in some engines.
  for (let i = 0; i < bytes.byteLength; i += 8192) {
    binary += String.fromCharCode(...bytes.subarray(i, i + 8192));
  }
  return btoa(binary);
}

/**
 * ScriptProcessorNode-based fallback path.
 *
 * Resamples Float32 → 24 kHz PCM16 synchronously on the main thread.
 * Deprecated by the spec but still widely supported.
 * Returns a cleanup callback.
 */
function startScriptProcessorFallback(
  ctx: AudioContext,
  source: MediaStreamAudioSourceNode,
  onChunk: (base64: string) => void,
  onLevel: (level: number) => void,
): () => void {
  const BUFFER_SIZE = 4096; // frames per ScriptProcessor callback
  // Cast to keep TypeScript happy (interface still in DOM lib).
  const processor = ctx.createScriptProcessor(BUFFER_SIZE, 1, 1);

  const ratio = ctx.sampleRate / TARGET_RATE;
  const outputFrames = Math.round(BUFFER_SIZE / ratio);
  const pcmChunk = new Int16Array(outputFrames);
  let phase = 0;

  processor.onaudioprocess = (event: AudioProcessingEvent) => {
    const input = event.inputBuffer.getChannelData(0);
    let rmsSum = 0;

    for (let outIdx = 0; outIdx < outputFrames; outIdx++) {
      const inPos = phase + outIdx * ratio;
      const lower = Math.floor(inPos);
      const upper = Math.min(lower + 1, input.length - 1);
      const frac = inPos - lower;
      const sample = (input[lower]! * (1 - frac) + input[upper]! * frac);

      const clamped = Math.max(-1, Math.min(1, sample));
      pcmChunk[outIdx] =
        clamped < 0 ? Math.round(clamped * 32_768) : Math.round(clamped * 32_767);
      rmsSum += sample * sample;
    }

    const rms = Math.sqrt(rmsSum / outputFrames);
    onLevel(Math.min(1, rms * 3));
    onChunk(pcm16BufferToBase64(pcmChunk.buffer.slice(0)));
  };

  source.connect(processor);
  // Connect to destination to keep the graph active (silent output).
  processor.connect(ctx.destination);

  return () => {
    processor.disconnect();
    source.disconnect();
  };
}

// ── Hook ──────────────────────────────────────────────────────────────────

/**
 * Capture microphone audio and stream PCM16 chunks at 24 kHz.
 *
 * @param options.onAudioChunk   Receives a Base64-encoded 100 ms PCM16 chunk.
 * @param options.onLevelChange  Optional; receives RMS level on each DSP block.
 */
export function useRealtimeAudio(
  options: UseRealtimeAudioOptions,
): UseRealtimeAudioResult {
  const { onAudioChunk, onLevelChange } = options;

  const [isCapturing, setIsCapturing] = useState(false);
  const [inputLevel, setInputLevel] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // Refs so stopCapture can tear down without stale closures.
  const audioCtxRef = useRef<AudioContext | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const workletNodeRef = useRef<AudioWorkletNode | null>(null);
  const cleanupFallbackRef = useRef<(() => void) | null>(null);

  // ── Stop ────────────────────────────────────────────────────────────────

  const stopCapture = useCallback(() => {
    // Disconnect worklet or ScriptProcessor.
    if (workletNodeRef.current) {
      workletNodeRef.current.port.onmessage = null;
      workletNodeRef.current.disconnect();
      workletNodeRef.current = null;
    }
    if (cleanupFallbackRef.current) {
      cleanupFallbackRef.current();
      cleanupFallbackRef.current = null;
    }
    // Stop all microphone tracks.
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    // Close AudioContext.
    void audioCtxRef.current?.close();
    audioCtxRef.current = null;

    setIsCapturing(false);
    setInputLevel(0);
  }, []);

  // ── Start ────────────────────────────────────────────────────────────────

  const startCapture = useCallback(async () => {
    // Reset any previous error.
    setError(null);

    // Clean up any previous session.
    stopCapture();

    // ── 1. Request microphone ──────────────────────────────────────────
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          // Request 24 kHz directly; the browser may honour or ignore this.
          sampleRate: TARGET_RATE,
        },
      });
    } catch (err) {
      const msg =
        err instanceof DOMException && err.name === 'NotAllowedError'
          ? 'Mikrofon-Zugriff verweigert. Bitte erlauben Sie den Zugriff in den Browser-Einstellungen.'
          : `Mikrofon konnte nicht geöffnet werden: ${err instanceof Error ? err.message : String(err)}`;
      setError(msg);
      return;
    }

    streamRef.current = stream;

    // ── 2. Create AudioContext ──────────────────────────────────────────
    // Prefer 24 kHz; if the browser ignores it the worklet/fallback resamples.
    const ctx = new AudioContext({ sampleRate: TARGET_RATE });
    audioCtxRef.current = ctx;

    const sourceNode = ctx.createMediaStreamSource(stream);

    // ── 3. Level → state helper ──────────────────────────────────────────
    const handleLevel = (level: number) => {
      setInputLevel(level);
      onLevelChange?.(level);
    };

    // ── 4. Prefer AudioWorklet, fall back to ScriptProcessorNode ─────────
    const useWorklet = typeof AudioWorkletNode !== 'undefined';

    if (useWorklet) {
      try {
        await ctx.audioWorklet.addModule(audioProcessorUrl);
        const workletNode = new AudioWorkletNode(ctx, 'pcm16-processor', {
          numberOfInputs: 1,
          numberOfOutputs: 0, // no audio output — processing only
          channelCount: 1,
          channelCountMode: 'explicit',
          channelInterpretation: 'discrete',
        });

        workletNode.port.onmessage = (event: MessageEvent<unknown>) => {
          const msg = event.data as { type: string; buffer?: ArrayBuffer; rms?: number };
          if (msg.type === 'chunk' && msg.buffer) {
            onAudioChunk(pcm16BufferToBase64(msg.buffer));
          } else if (msg.type === 'level' && typeof msg.rms === 'number') {
            handleLevel(msg.rms);
          }
        };

        sourceNode.connect(workletNode);
        // Do NOT connect workletNode → destination (no audio output needed).
        workletNodeRef.current = workletNode;
      } catch (err) {
        // AudioWorklet failed (e.g. HTTP or unsupported) — fall through to
        // ScriptProcessorNode.
        logger.warn('useRealtimeAudio', 'AudioWorklet failed, using fallback', err);
        cleanupFallbackRef.current = startScriptProcessorFallback(
          ctx, sourceNode, onAudioChunk, handleLevel,
        );
      }
    } else {
      // AudioWorkletNode not available (e.g. older Safari).
      cleanupFallbackRef.current = startScriptProcessorFallback(
        ctx, sourceNode, onAudioChunk, handleLevel,
      );
    }

    // Resume context if it was suspended (browser autoplay policy).
    if (ctx.state === 'suspended') {
      await ctx.resume();
    }

    setIsCapturing(true);
  }, [stopCapture, onAudioChunk, onLevelChange]);

  return { startCapture, stopCapture, isCapturing, inputLevel, error };
}
